import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { HealthStatus } from '@pazportal/types';
import {
  useAllCustomersWithSignals,
  compareScore,
  coverageLabel,
  formatHealthScore,
  HEALTH_STATUS_LABEL,
  type CustomerWithSignals,
} from '../hooks/useAllCustomersWithSignals';
import { HealthBar, HealthPill, SignalChips } from '../components/HealthSignals';
import { formatCurrency, formatSlaTier } from '../lib/formatters';

type SortKey = 'name' | 'score' | 'mrr' | 'renewal' | 'tickets' | 'sla_tier';
type SortDir = 'asc' | 'desc';
type Filter = 'all' | HealthStatus;

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'critical', label: 'Critical' },
  { key: 'degraded', label: 'Degraded' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'unknown', label: 'No data' },
];

export function CustomersPage() {
  const { data: customers, isLoading, isError } = useAllCustomersWithSignals();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    let list = customers ?? [];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(q) || c.account_number.toLowerCase().includes(q));
    }

    if (filter !== 'all') list = list.filter((c) => c.health.signals.status === filter);

    list = [...list].sort((a, b) => {
      let result = 0;
      switch (sortKey) {
        case 'name': result = a.name.toLowerCase().localeCompare(b.name.toLowerCase()); break;
        case 'score': result = compareScore(a.health.signals.score, b.health.signals.score); break;
        case 'mrr': result = a.mrr_cents - b.mrr_cents; break;
        case 'renewal': result = (a.renewalDays ?? Number.POSITIVE_INFINITY) - (b.renewalDays ?? Number.POSITIVE_INFINITY); break;
        case 'tickets': result = a.openTicketCount - b.openTicketCount; break;
        case 'sla_tier': result = a.sla_tier.localeCompare(b.sla_tier); break;
      }
      // Unknown scores stay last regardless of direction.
      if (sortKey === 'score' && (a.health.signals.score === null || b.health.signals.score === null)) return result;
      return sortDir === 'asc' ? result : -result;
    });

    return list;
  }, [customers, search, filter, sortKey, sortDir]);

  const coverage = useMemo(() => {
    const list = customers ?? [];
    const stored = list.filter((c) => c.health.source === 'stored').length;
    const unknown = list.filter((c) => c.health.signals.score === null).length;
    const withUptime = list.filter((c) => c.health.inputs.uptimePct24h !== null).length;
    const withAuvik = list.filter((c) => c.health.inputs.auvikConnected).length;
    return { total: list.length, stored, computed: list.length - stored, unknown, withUptime, withAuvik };
  }, [customers]);

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  }, [sortKey]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);

  const toggleAll = useCallback(() => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((c) => c.id)));
  }, [selected.size, filtered]);

  const exportCsv = useCallback((onlySelected: boolean) => {
    const rowsSource = onlySelected ? filtered.filter((c) => selected.has(c.id)) : filtered;
    const headers = ['Name', 'Account', 'Health Score', 'Health Status', 'Signals', 'Health Source', 'MRR (USD)', 'SLA Tier', 'Open Tickets', 'Past SLA', 'Renewal Days'];
    const rows = rowsSource.map((c) => [
      c.name,
      c.account_number,
      formatHealthScore(c.health.signals.score),
      HEALTH_STATUS_LABEL[c.health.signals.status],
      coverageLabel(c.health.signals.coverage),
      c.health.source === 'stored' ? 'stored' : 'computed now',
      (c.mrr_cents / 100).toFixed(2),
      c.sla_tier,
      String(c.openTicketCount),
      String(c.breachedTicketCount),
      c.renewalDays === null ? '' : String(c.renewalDays),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = onlySelected ? 'customers-selected.csv' : `customers-${filter}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filtered, selected, filter]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading customers</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-border bg-surface p-6 text-sm text-notification" role="alert">
        Could not load the customer roster. Refresh to try again.
      </div>
    );
  }

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <span className="text-text-faint ml-1" aria-hidden="true">{'↕'}</span>;
    return <span className="ml-1" aria-hidden="true">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const sortButton = (k: SortKey, label: string) => (
    <button
      type="button"
      onClick={() => toggleSort(k)}
      aria-sort={sortKey === k ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
      className="inline-flex items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {label} <SortIcon k={k} />
    </button>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-text">Customer Roster</h1>
        <p className="text-sm text-text-muted mt-1">
          {coverage.total} customer{coverage.total === 1 ? '' : 's'}.
          {' '}Health is {coverage.stored > 0 ? `stored for ${coverage.stored} and computed now for ${coverage.computed}` : 'computed now for every customer (the hourly scoring job has not written a fresh score)'}.
          {' '}Signals available today: tickets, SLA deadlines, renewal dates
          {coverage.withUptime > 0 ? `, UniFi uptime for ${coverage.withUptime}` : ''}
          {coverage.withAuvik > 0 ? `, Auvik alerts for ${coverage.withAuvik}` : ''}.
          {' '}MRR history is not tracked, so 4 of 5 signals is the maximum.
          {coverage.unknown > 0 ? ` ${coverage.unknown} customer${coverage.unknown === 1 ? ' has' : 's have'} no data yet.` : ''}
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <label className="w-full sm:w-64">
          <span className="sr-only">Search customers</span>
          <input
            type="search"
            placeholder="Search name or account…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-1.5 text-sm border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary min-h-[44px] md:min-h-[40px]"
          />
        </label>
        <div className="flex gap-1 overflow-x-auto max-w-full" role="group" aria-label="Filter by health status">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={`px-3 py-1.5 min-h-[44px] md:min-h-0 text-xs font-medium rounded-md whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                filter === f.key
                  ? 'bg-primary text-text-inverse'
                  : 'bg-surface-3 text-text-muted hover:bg-surface-strong'
              }`}
            >{f.label}</button>
          ))}
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => exportCsv(false)}
          className="px-3 py-1.5 min-h-[44px] md:min-h-0 text-xs font-medium rounded-md bg-primary text-text-inverse hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Export CSV ({filtered.length})
        </button>
      </div>

      {/* Bulk selection bar */}
      {selected.size > 0 && (
        <div className="sticky top-[var(--paz-header-offset)] z-10 flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-md bg-primary-soft border border-primary/30">
          <span className="text-sm font-medium text-text">{selected.size} selected</span>
          <button type="button" onClick={() => exportCsv(true)} className="text-xs font-medium px-2.5 py-1 min-h-[36px] rounded bg-surface border border-border text-text-muted hover:text-text">Export selected</button>
          <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-text-muted hover:text-text ml-auto min-h-[36px]">Clear</button>
        </div>
      )}

      {/* Mobile: card list */}
      <div className="md:hidden space-y-2">
        {filtered.length === 0 && (
          <div className="rounded-lg border border-border bg-surface p-6 text-center text-sm text-text-muted">
            No customers match your filters.
          </div>
        )}
        {filtered.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => navigate(`/customers/${c.id}`)}
            className="w-full text-left rounded-lg border border-border bg-surface p-3 active:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-text truncate">{c.name}</p>
                <p className="text-xs text-text-faint">{c.account_number}</p>
              </div>
              <HealthPill health={c.health} className="shrink-0" />
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div>
                <p className="text-text-faint">MRR</p>
                <p className="text-text">{formatCurrency(c.mrr_cents)}</p>
              </div>
              <div>
                <p className="text-text-faint">Tickets</p>
                <p className="text-text">{c.openTicketCount}{c.breachedTicketCount > 0 ? ` (${c.breachedTicketCount} past SLA)` : ''}</p>
              </div>
              <div>
                <p className="text-text-faint">Renewal</p>
                <p className="text-text">{c.renewalDays === null ? '—' : `${c.renewalDays}d`}</p>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <p className="text-xs text-text-faint">{formatSlaTier(c.sla_tier)} · {coverageLabel(c.health.signals.coverage)}</p>
              <SignalChips health={c.health} />
            </div>
          </button>
        ))}
      </div>

      {/* Data table (desktop / tablet) */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-3 text-left">
              <th className="px-3 py-2.5 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all customers"
                  checked={selected.size === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                  className="rounded border-border"
                />
              </th>
              <th className="px-3 py-2.5">{sortButton('name', 'Customer')}</th>
              <th className="px-3 py-2.5 text-center w-32">{sortButton('score', 'Health')}</th>
              <th className="px-3 py-2.5 text-center w-32">Signals</th>
              <th className="px-3 py-2.5 text-right w-24">{sortButton('mrr', 'MRR')}</th>
              <th className="px-3 py-2.5 text-center w-24">Sites / Deploys</th>
              <th className="px-3 py-2.5 text-center w-24">{sortButton('renewal', 'Renewal')}</th>
              <th className="px-3 py-2.5 text-center w-24">{sortButton('sla_tier', 'SLA')}</th>
              <th className="px-3 py-2.5 text-center w-20">{sortButton('tickets', 'Tickets')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-divider">
            {filtered.map((c) => (
              <CustomerRow key={c.id} customer={c} isSelected={selected.has(c.id)} onToggle={() => toggleSelect(c.id)} onClick={() => navigate(`/customers/${c.id}`)} />
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-text-muted">No customers match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CustomerRow({ customer: c, isSelected, onToggle, onClick }: { customer: CustomerWithSignals; isSelected: boolean; onToggle: () => void; onClick: () => void }) {
  const renewalDays = c.renewalDays;
  const renewalColor = renewalDays === null ? 'text-text-muted' : renewalDays < 30 ? 'text-error' : renewalDays < 60 ? 'text-warning' : 'text-text';

  return (
    <tr className="bg-surface hover:bg-surface-strong transition-colors cursor-pointer" onClick={onClick}>
      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" aria-label={`Select ${c.name}`} checked={isSelected} onChange={onToggle} className="rounded border-border" />
      </td>
      <td className="px-3 py-2.5">
        <p className="font-medium text-text">{c.name}</p>
        <p className="text-xs text-text-faint">{c.account_number}</p>
      </td>
      <td className="px-3 py-2.5 text-center">
        <HealthPill health={c.health} showLabel={false} />
        <HealthBar health={c.health} className="mt-1 mx-auto" />
        <p className="text-[10px] text-text-faint mt-1">{c.health.signals.score === null ? 'No signals yet' : coverageLabel(c.health.signals.coverage)}</p>
      </td>
      <td className="px-3 py-2.5 text-center">
        <SignalChips health={c.health} />
      </td>
      <td className="px-3 py-2.5 text-right font-medium text-text">{formatCurrency(c.mrr_cents)}</td>
      <td className="px-3 py-2.5 text-center text-text-muted">{c.locationCount} / {c.deploymentCount}</td>
      <td className={`px-3 py-2.5 text-center font-medium ${renewalColor}`}>
        {renewalDays !== null ? `${renewalDays}d` : '—'}
      </td>
      <td className="px-3 py-2.5 text-center">
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-surface-3 text-text-muted">
          {formatSlaTier(c.sla_tier)}
        </span>
      </td>
      <td className="px-3 py-2.5 text-center">
        <span className={`font-medium ${c.breachedTicketCount > 0 ? 'text-error' : c.openTicketCount > 5 ? 'text-warning' : 'text-text'}`}>
          {c.openTicketCount}
        </span>
        {c.breachedTicketCount > 0 && <p className="text-[10px] text-error">{c.breachedTicketCount} past SLA</p>}
      </td>
    </tr>
  );
}
